import WarehouseService from '@/api/WarehouseService'
import StockService from '@/api/StockService'
import GoodsReceiptService from '@/api/GoodsReceiptService'
import GoodsIssueService from '@/api/GoodsIssueService'
import GoodsTransferService from '@/api/GoodsTransferService'

const transformUsedLotSerialSkus = (data = {}) => {
  return {
    'usedLotSkuIds': data.used_lot_sku_ids || [],
    'usedSerialSkuIds': data.used_serial_sku_ids || [],
  }
}

// initial state
const state = {
  warehouses: [],
  warehouse: {},

  stocks: [],
  refreshStocks: 0,

  activities: [],

  goodsreceipts: [],
  goodsreceipt: null,
  nextGrCode: null,

  goodsissues: [],
  goodsissue: null,
  nextGiCode: null,

  goodstransfers: [],
  goodstransfer: null,
  nextGtCode: null,

  todo: [],
  currentstocks: [],
  levelSkus: [],
  usedLotSerialSkus: transformUsedLotSerialSkus({}),
}

// getters
const getters = {
  getWarehouses: state => state.warehouses,
  getWarehouse: state => state.warehouse,
  getStocks: state => state.stocks,
  getRefreshStocks: state => state.refreshStocks,
  getActivities: state => state.activities,
  getGoodsreceipts: state => state.goodsreceipts,
  getGoodsreceipt: state => state.goodsreceipt,
  getNextGrCode: state => state.nextGrCode,
  getGoodsissues: state => state.goodsissues,
  getGoodsissue: state => state.goodsissue,
  getNextGiCode: state => state.nextGiCode,
  getGoodstransfers: state => state.goodstransfers,
  getGoodstransfer: state => state.goodstransfer,
  getNextGtCode: state => state.nextGtCode,
  getTodo: state => state.todo,
  getCurrentstocks: state => state.currentstocks,
  getLevelSkus: state => state.levelSkus,
  getUsedLotSerialSkus: state => state.usedLotSerialSkus,
  getMainWarehouse: state => {
    return state.warehouses.find(wh => wh.primary === true)
  },
  getMainStock: (state) => (stocks) => {
    let wh = state.warehouses.find(wh => wh.primary === true)
    if (wh) {
      return stocks.find(stock => stock.warehouse_id === wh.warehouse_id)
    }
    return null
  },
  getWarehouseInfo: (state) => (warehouse_id) => {
    return state.warehouses.find(wh => wh.warehouse_id === warehouse_id) || {}
  },
  getPercentageOfGoodsReceipt: (state) => (goodsreceipts, total) => {
    return 0
  },
  getGoodsReceiptStatus: (state) => (goodsreceipt, $t) => {
    if (goodsreceipt.status === 'padding') {
      return {
        message: $t('status.gr.padding'),
        status: 'uncompleted',
      }
    }
    return {
      message: $t('status.gr.done'),
      status: 'completed',
    }
  },
  getGoodsIssueStatus: (state) => (goodsissue, $t) => {
    if (goodsissue.status === 'padding') {
      return {
        message: $t('status.gi.padding'),
        status: 'uncompleted',
      }
    }
    return {
      message: $t('status.gi.done'),
      status: 'completed',
    }
  },
  getGoodsTransferStatus: (state) => (goodstransfer, $t) => {
    if (goodstransfer.status === 'padding') {
      return {
        message: $t('status.gt.padding'),
        status: 'uncompleted',
      }
    }
    return {
      message: $t('status.gt.done'),
      status: 'completed',
    }
  },
  isAssignedInWarehouse: (state) => (skuId, whId) => {
    return state.warehouses.some((wh) => {
      return wh.warehouse_id === whId && wh.assign_skus.some((sku) => sku.sku_id === skuId)
    })
  },
  getBinName: (state) => (binId) => {
    return state.warehouses.reduce((acc, cur) => {
      const bin = cur.bin_locations.find((item) => item._id === binId)
      if (bin) {
        acc = bin.code
      }
      return acc
    }, null)
  },
  isAssignedSkuToStock: (state) => (whId, skuId) => {
    for (const warehouse of state.warehouses) {
      if (warehouse.warehouse_id === whId) {
        for (const assignSku of warehouse.assign_skus) {
          if (assignSku.sku_id === skuId) {
            return true
          }
        }
      }
    }
    return false
  },
  getWhIdByCode: (state) => (whCode) => {
    const wh = state.warehouses.find((item) => item.short_name === whCode)
    return wh ? wh.warehouse_id : null
  },
  isSkuAssignInWh: (state) => (skuId, whId) => {
    const wh = state.warehouses.find((item) => item.warehouse_id === whId)
    if (!wh) {
      return false
    }
    for (const assignSku of wh.assign_skus) {
      if (assignSku.sku_id === skuId) {
        return true
      }
    }
    return false
  },
  getBinIdBy: (state) => (binCode, whCode) => {
    const wh = state.warehouses.find((item) => item.short_name === whCode)
    if (!wh) {
      return null
    }
    const bin = wh.bin_locations.find((item) => item.code === binCode)
    return bin ? bin._id : null
  },
  getBinNameBy: (state) => (binId) => {
    return state.warehouses.reduce((acc, cur) => {
      cur.bin_locations.forEach((binItem) => {
        if (binItem._id === binId) {
          acc = binItem.code
        }
      })
      return acc
    }, null)
  },
  getWhLocationName: (state) => (whId, binId) => {
    return state.warehouses.reduce((acc, cur) => {
      if (cur.warehouse_id === whId) {
        acc = cur.warehouse_name
        const bin = cur.bin_locations.find((binItem) => binItem._id === binId)
        if (bin) {
          acc += ` - ${bin.code}`
        }
      }
      return acc
    }, null)
  },
  getBinIdRecommended: (state) => (stockLocations) => {
    const bins = stockLocations.filter((stockLocation) => stockLocation.bin_location_id != null && stockLocation.instock != 0)
    if (bins.length === 1) {
      return bins[0].bin_location_id
    }
    return null
  },
  isBinIdExist: (state) => (binId) => {
    return state.warehouses.reduce((acc, cur) => {
      cur.bin_locations.forEach((binItem) => {
        if (binItem._id === binId) {
          acc = true
        }
      })
      return acc
    }, false)
  },
  hasStock: (state) => (skuId) => {
    for (const warehouse of state.warehouses) {
      for (const assignSku of warehouse.assign_skus) {
        if (assignSku.sku_id === skuId) {
          return true
        }
      }
    }
    return false
  },
  hasStockIn: (state) => (skuId, whId) => {
    for (const warehouse of state.warehouses) {
      if (warehouse.warehouse_id === whId) {
        for (const assignSku of warehouse.assign_skus) {
          if (assignSku.sku_id === skuId) {
            return true
          }
        }
      }
    }
    return false
  },
}

// mutations
const mutations = {
  setWarehouses (state, warehouses) {
    state.warehouses = warehouses
  },
  setWarehouse (state, warehouse) {
    state.warehouse = warehouse
  },
  setStocks (state, stocks) {
    state.stocks = stocks
  },
  setActivities (state, activities) {
    state.activities = activities
  },
  trickRefreshStocks (state, activities) {
    state.refreshStocks++
  },
  setGoodsreceipts (state, data) {
    state.goodsreceipts = data
  },
  setGoodsreceipt (state, data) {
    state.goodsreceipt = data
  },
  setNextGrCode (state, data) {
    state.nextGrCode = data
  },
  setGoodsissues (state, data) {
    state.goodsissues = data
  },
  setGoodsissue (state, data) {
    state.goodsissue = data
  },
  setNextGiCode (state, data) {
    state.nextGiCode = data
  },
  setGoodstransfers (state, data) {
    state.goodstransfers = data
  },
  setGoodstransfer (state, data) {
    state.goodstransfer = data
  },
  setNextGtCode (state, data) {
    state.nextGtCode = data
  },
  setTodo (state, data) {
    state.todo = data
  },
  setCurrentstocks (state, data) {
    state.currentstocks = data
  },
  setSkuLevel (state, data) {
    state.levelSkus = data
  },
  setUsedLotSerial (state, data) {
    state.usedLotSerialSkus = transformUsedLotSerialSkus(data)
  },
}

// actions
const actions = {
  async getWarehouses ({ commit }) {
    var result

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getWarehouses') >= 0) return

    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getWarehouses')

      let response = await WarehouseService.getAll()

      // start loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getWarehouses')

      result = response.data

      if (result.success) {
        commit('setWarehouses', result.data)
      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  clearWarehouses ({ commit }) {
    commit('setWarehouses', [])
  },
  async getWarehouse ({ commit }, id) {
    var result

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getWarehouse') >= 0) return

    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getWarehouse')

      let response = await WarehouseService.getSingle({ warehouse_id: id })

      // start loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getWarehouse')

      result = response.data

      if (result.success && result.data.length) {
        commit('setWarehouse', result.data[0])
      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async getStocks ({ commit }, params) {
    var result

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('setStocks') >= 0) return

    try {
      // clear stocks
      commit('setStocks', [])
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'setStocks')

      let response = await StockService.getStock(params)

      // stop loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'setStocks')

      result = response.data

      if (result.success) {
        commit('setStocks', result.data)
        this.dispatch('setPagination', result.meta)
      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async getStocksBySkus ({ commit }, params) {
    var result

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getStocksBySkus') >= 0) return

    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getStocksBySkus')

      let response = await StockService.getStocksBySkus(params)

      // stop loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getStocksBySkus')

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  clearStock ({ commit }) {
    commit('setStocks', [])
  },
  async getAllStocks ({ commit }, params) {
    var result

    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await StockService.getStock(params)

      result = response.data

      // start loading
      this.dispatch('Loading', false)

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async getActivities ({ commit }, params = {}) {
    var result

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getActivities') >= 0) return

    try {
      // clear stocks
      commit('setStocks', [])
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getActivities')

      let response = await StockService.getActivities(params)

      // stop loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getActivities')

      result = response.data

      if (result.success) {
        commit('setActivities', result.data)
        this.dispatch('setPagination', result.meta)
      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  clearActivities ({ commit }) {
    commit('setActivities', [])
  },
  async getTodo ({ commit }) {
    var result = null

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getTodo') >= 0) return

    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getTodo')

      let response = await StockService.getTodo()

      // stop loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getTodo')

      result = response.data

      if (result.success) {
        commit('setTodo', result.data)
      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  clearTodo ({ commit }) {
    commit('setTodo', [])
  },
  async getCurrentstocks ({ commit }, params = {}) {
    var result = null

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getCurrentstocks') >= 0) return

    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getCurrentstocks')

      let response = await StockService.getCurrentstocks(params)

      // stop loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getCurrentstocks')

      result = response.data

      if (result.success) {
        commit('setCurrentstocks', result.data)
        this.dispatch('setPagination', result.meta)
      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async getSkuLevel ({ commit }, params = {}) {
    var result = null

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getSkuLevel') >= 0) return

    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getSkuLevel')

      let response = await StockService.getSkuLevel(params)

      // stop loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getSkuLevel')

      result = response.data

      if (result.success) {
        commit('setSkuLevel', result.data)
        this.dispatch('setPagination', result.meta)
      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async getUsedLotSerialSkus ({ commit }) {
    var result = null

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getUsedLotSerialSkus') >= 0) return

    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getSgetUsedLotSerialSkuskuLevel')

      let response = await StockService.getUsedLotSerialSkus()

      // stop loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getUsedLotSerialSkus')

      result = response.data

      if (result.success) {
        commit('setUsedLotSerial', result.data)
      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  clearCurrentstocks ({ commit }) {
    commit('setCurrentstocks', [])
  },
  async updateStock ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await StockService.updateStock(data)

      result = response.data

      // stop loading
      this.dispatch('Loading', false)

      // refreshStocks
      this.dispatch('trickRefreshStocks')
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async updateStockTransfer ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await StockService.updateStockTransfer(data)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }

      // stop loading
      this.dispatch('Loading', false)

      // refreshStocks
      this.dispatch('trickRefreshStocks')
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async updateStockWarning ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await StockService.updateStockWarning(data)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }

      // stop loading
      this.dispatch('Loading', false)

      // refreshStocks
      this.dispatch('trickRefreshStocks')
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async updateStockBin ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await StockService.updateStockBin(data)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }

      // stop loading
      this.dispatch('Loading', false)

      // refreshStocks
      this.dispatch('trickRefreshStocks')
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  trickRefreshStocks ({ commit }) {
    commit('trickRefreshStocks')
  },
  async insertGoodsreceipt ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await GoodsReceiptService.insert(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // clear error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // clear error
      this.dispatch('Error', e)
    }
    return result
  },
  async getGoodsreceipts ({ commit }, params = {}) {
    var result = null

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getGoodsreceipts') >= 0) return

    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getGoodsreceipts')

      let response = await GoodsReceiptService.getAll(params)

      // stop loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getGoodsreceipts')

      result = response.data

      if (result.success) {
        commit('setGoodsreceipts', result.data)
        this.dispatch('setPagination', result.meta)
      } else {
        // clear error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // clear error
      this.dispatch('Error', e)
    }
    return result
  },
  clearGoodsreceipts ({ commit }) {
    commit('setGoodsreceipts', [])
  },
  async getGoodsreceipt ({ commit }, data) {
    var result = null

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getGoodsreceipt') >= 0) return

    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getGoodsreceipt')

      let response = await GoodsReceiptService.getSingle({
        gr_id: data.gr_id,
      })

      // stop loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getGoodsreceipt')

      result = response.data

      if (result.success) {
        if (result.data.length > 0) {
          commit('setGoodsreceipt', result.data[0])
        } else {
          // not found data
          this.dispatch('Error', 'Not found data')
        }
      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  clearGoodsreceipt ({ commit }) {
    commit('setGoodsreceipt', null)
  },
  async updateGoodsreceipt ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await GoodsReceiptService.update(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async doneGoodsreceipt ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await GoodsReceiptService.done(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async deleteGoodsreceipt ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await GoodsReceiptService.delete(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async getNextGrCode ({ commit }, data) {
    var result = null

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getNextGrCode') >= 0) return

    try {
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getNextGrCode')

      let response = await GoodsReceiptService.getLatestCode(data)

      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getNextGrCode')

      result = response.data

      if (result.success) {
        commit('setNextGrCode', result.data.next)
      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async insertGoodsissue ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await GoodsIssueService.insert(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // clear error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // clear error
      this.dispatch('Error', e)
    }
    return result
  },
  async getGoodsissues ({ commit }, params = {}) {
    var result = null

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getGoodsissues') >= 0) return

    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getGoodsissues')

      let response = await GoodsIssueService.getAll(params)

      // stop loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getGoodsissues')

      result = response.data

      if (result.success) {
        commit('setGoodsissues', result.data)
        this.dispatch('setPagination', result.meta)
      } else {
        // clear error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // clear error
      this.dispatch('Error', e)
    }
    return result
  },
  clearGoodsissues ({ commit }) {
    commit('setGoodsissues', [])
  },
  async getGoodsissue ({ commit }, data) {
    var result

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getGoodsissue') >= 0) return

    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getGoodsissue')

      let response = await GoodsIssueService.getSingle({
        gi_id: data.gi_id,
      })

      // stop loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getGoodsissue')

      result = response.data

      if (result.success) {
        if (result.data.length > 0) {
          commit('setGoodsissue', result.data[0])
        } else {
          // not found data
          this.dispatch('Error', 'Not found data')
        }
      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  clearGoodsissue ({ commit }) {
    commit('setGoodsissue', null)
  },
  async updateGoodsissue ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await GoodsIssueService.update(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async doneGoodsissue ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await GoodsIssueService.done(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async deleteGoodsissue ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await GoodsIssueService.delete(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async getNextGiCode ({ commit }, data) {
    var result

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getNextGiCode') >= 0) return

    try {
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getNextGiCode')

      let response = await GoodsIssueService.getLatestCode(data)

      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getNextGiCode')

      result = response.data

      if (result.success) {
        commit('setNextGiCode', result.data.next)
      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async insertGoodstransfer ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await GoodsTransferService.insert(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // clear error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // clear error
      this.dispatch('Error', e)
    }
    return result
  },
  async getGoodstransfers ({ commit }, params = {}) {
    var result = null

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getGoodstransfers') >= 0) return

    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getGoodstransfers')

      let response = await GoodsTransferService.getAll(params)

      // stop loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getGoodstransfers')

      result = response.data

      if (result.success) {
        commit('setGoodstransfers', result.data)
        this.dispatch('setPagination', result.meta)
      } else {
        // clear error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // clear error
      this.dispatch('Error', e)
    }
    return result
  },
  clearGoodstransfers ({ commit }) {
    commit('setGoodstransfers', [])
  },
  async getGoodstransfer ({ commit }, data) {
    var result

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getGoodstransfer') >= 0) return

    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getGoodstransfer')

      let response = await GoodsTransferService.getSingle({
        gt_id: data.gt_id,
      })

      // stop loading
      this.dispatch('Loading', false)
      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getGoodstransfer')

      result = response.data

      if (result.success) {
        if (result.data.length > 0) {
          commit('setGoodstransfer', result.data[0])
        } else {
          // not found data
          this.dispatch('Error', 'Not found data')
        }
      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  clearGoodstransfer ({ commit }) {
    commit('setGoodstransfer', null)
  },
  async updateGoodstransfer ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await GoodsTransferService.update(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async doneGoodstransfer ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await GoodsTransferService.done(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async deleteGoodstransfer ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await GoodsTransferService.delete(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async getNextGtCode ({ commit }, data) {
    var result

    // check this api is still loading or not
    if (this.state.app.loadinglist.indexOf('getNextGtCode') >= 0) return

    try {
      // clear error
      this.dispatch('Error', null)
      // add loadinglist
      this.dispatch('addLoadinglist', 'getNextGtCode')

      let response = await GoodsTransferService.getLatestCode(data)

      // remove loadinglist
      this.dispatch('removeLoadinglist', 'getNextGtCode')

      result = response.data

      if (result.success) {
        commit('setNextGtCode', result.data.next)
      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async insertWarehouse ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await WarehouseService.insert(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async updateWarehouse ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await WarehouseService.update(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async updateWarehouseAssignSkus ({ commit }, { id, data }) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await WarehouseService.updateAssignSkus(id, data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async deleteWarehouse ({ commit }, data) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await WarehouseService.delete(data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async insertWarehouseBins ({ commit }, { id, data }) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await WarehouseService.insertBins(id, data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async updateWarehouseBins ({ commit }, { id, data }) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await WarehouseService.updateBins(id, data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  async deleteWarehouseBins ({ commit }, { id, data }) {
    var result = null
    try {
      // start loading
      this.dispatch('Loading', true)
      // clear error
      this.dispatch('Error', null)

      let response = await WarehouseService.deleteBins(id, data)

      // stop loading
      this.dispatch('Loading', false)

      result = response.data

      if (result.success) {

      } else {
        // error
        this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
      }
    } catch (e) {
      // error
      this.dispatch('Error', e)
    }
    return result
  },
  clearNextGrCode ({ commit }) {
    commit('setNextGrCode', null)
  },
  clearNextGiCode ({ commit }) {
    commit('setNextGiCode', null)
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}



// WEBPACK FOOTER //
// ./src/store/modules/Inventory.js